import { Segmented, Select } from 'antd';
import OpterraPhoenixLogo from 'assets/images/OpterraPhoenixLogo.png';
import Notification from 'components/common/Notification';
import {
  SUMMARY_DATA_BASIS_ACTUAL,
  SUMMARY_DATA_BASIS_LABELS,
  SUMMARY_DATA_BASIS_NO_DATA_MESSAGE,
  SUMMARY_DATA_BASIS_PLAN,
  SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL,
  SUMMARY_INPUT_VIEW_CHART,
  SUMMARY_INPUT_VIEW_STORAGE_KEY,
  SUMMARY_INPUT_VIEW_TABLE,
  SUMMARY_METRIC_COST_PER_HA,
  SUMMARY_METRIC_QUANTITY,
  SUMMARY_METRIC_TOTAL_AREA,
  SUMMARY_METRIC_TOTAL_COST,
  SUMMARY_NO_DATA_MESSAGE,
  SUMMARY_INPUT_SUMMARIES_SECTION_ID,
  SUMMARY_PLAN_VS_ACTUAL_SECTION_ID,
  SUMMARY_SCOPE_HINT_STORAGE_KEY,
  SUMMARY_TEMPORAL_FLAT,
  SUMMARY_TEMPORAL_MONTHLY,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput } from 'interfaces/IInput';
import WorkspaceEmptyState from 'pages/FarmsPage/Workspace/WorkspaceEmptyState';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from 'redux/Store';
import type {
  SummaryDataBasis,
  SummaryDataMetric,
  SummaryInputView,
  SummaryTemporalLayout,
} from '../../../../../redux/slices/InsightsSlice';
import {
  setSummaryDataBasis,
  setSummaryHintVisible,
  setSummaryInputView,
} from '../../../../../redux/slices/InsightsSlice';
import type { InsightsScopeLevel } from '../insightUtils';
import AreaSummaryTable from './AreaSummaryTable';
import { computeCostByCropBySide } from './computeCostByCrop';
import { computePlanVsActual } from './computePlanVsActual';
import {
  computeAreaSummary,
  computeSummaryReportBySide,
} from './computeSummaryReport';
import CostByCropTable from './CostByCropTable';
import { resolveDataSides } from './dataBasis';
import MonthlySectionTable from './MonthlySectionTable';
import PlanVsActualChart from './PlanVsActualChart';
import SummaryReportTable from './SummaryReportTable';

const METRIC_LABELS: Record<SummaryDataMetric, string> = {
  [SUMMARY_METRIC_QUANTITY]: 'Quantity',
  [SUMMARY_METRIC_TOTAL_AREA]: 'Total Area',
  [SUMMARY_METRIC_TOTAL_COST]: 'Total Cost',
  [SUMMARY_METRIC_COST_PER_HA]: '$/ha',
};

const METRIC_OPTIONS: { label: string; value: SummaryDataMetric }[] = [
  SUMMARY_METRIC_QUANTITY,
  SUMMARY_METRIC_TOTAL_AREA,
  SUMMARY_METRIC_TOTAL_COST,
  SUMMARY_METRIC_COST_PER_HA,
].map((value) => ({ label: METRIC_LABELS[value], value }));

/** Total Area isn't tracked per product row, so the chart view can't plot it. */
const CHART_METRIC_OPTIONS = METRIC_OPTIONS.filter(
  (option) => option.value !== SUMMARY_METRIC_TOTAL_AREA
);

const INPUT_VIEW_OPTIONS: { label: string; value: SummaryInputView }[] = [
  { label: 'Table', value: SUMMARY_INPUT_VIEW_TABLE },
  { label: 'Chart', value: SUMMARY_INPUT_VIEW_CHART },
];

const DATA_BASIS_OPTIONS: { label: string; value: SummaryDataBasis }[] = [
  SUMMARY_DATA_BASIS_PLAN,
  SUMMARY_DATA_BASIS_ACTUAL,
  SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL,
].map((value) => ({ label: SUMMARY_DATA_BASIS_LABELS[value], value }));

interface SummaryReportViewProps {
  inputs: IInput[];
  gUnits: GUnit[];
  /**
   * Which tree level the inputs resolve to. Gates which sections
   * render: Area Summary / Cost by Crop need several crops in view
   * (Org/Farm), while the per-crop spend timeline needs the opposite —
   * Paddock level or lower, where the scope is (largely) already one crop.
   */
  scopeLevel?: InsightsScopeLevel;
}

interface NavEntry {
  id: string;
  label: string;
}

const SummaryReportView: React.FC<SummaryReportViewProps> = ({
  inputs,
  gUnits,
  scopeLevel,
}) => {
  const dispatch = useDispatch();
  const { summaryHintVisible, summaryDataBasis, summaryInputView } = useSelector(
    (state: RootState) => state.insightsReducer
  );
  // Plan / Actual / both — Cost by Crop, Input summaries and Monthly
  // breakdown each render one column group per side, never a summed value.
  const sides = useMemo(
    () => resolveDataSides(summaryDataBasis),
    [summaryDataBasis]
  );

  const [temporalLayout, setTemporalLayout] = useState<SummaryTemporalLayout>(
    SUMMARY_TEMPORAL_FLAT
  );
  const [monthlyMetric, setMonthlyMetric] = useState<SummaryDataMetric>(
    SUMMARY_METRIC_TOTAL_COST
  );
  // Input summaries show each section as a table or a chart, never both
  // (OP-2394). The chart view plots per-product totals, so the
  // Total / Monthly Breakdown choice only applies to tables, and the chart
  // can't plot Total Area.
  const isChartView = summaryInputView === SUMMARY_INPUT_VIEW_CHART;
  const isMonthly = !isChartView && temporalLayout === SUMMARY_TEMPORAL_MONTHLY;
  const activeMetric =
    isChartView && monthlyMetric === SUMMARY_METRIC_TOTAL_AREA
      ? SUMMARY_METRIC_TOTAL_COST
      : monthlyMetric;

  const handleInputViewChange = useCallback(
    (value: SummaryInputView) => {
      dispatch(setSummaryInputView(value));
      try {
        localStorage.setItem(SUMMARY_INPUT_VIEW_STORAGE_KEY, value);
      } catch {
        // Storage unavailable (private mode / quota) — the choice still applies for this session.
      }
    },
    [dispatch]
  );

  const handleDataBasisChange = useCallback(
    (value: SummaryDataBasis) => dispatch(setSummaryDataBasis(value)),
    [dispatch]
  );

  const handleCloseScopeHint = useCallback(() => {
    localStorage.setItem(SUMMARY_SCOPE_HINT_STORAGE_KEY, 'false');
    dispatch(setSummaryHintVisible(false));
  }, [dispatch]);

  // Crop-comparison sections (Area, Cost by Crop) only make sense once
  // several crops are naturally in view, i.e. Org/Farm scope — at Paddock
  // level or a specific Plan, the scope is (largely) already one crop, so
  // they'd show a single trivial row. Defaults to shown when scopeLevel is
  // still unresolved (e.g. on first render), matching Org/Farm behaviour.
  const showCropLevelSections =
    scopeLevel !== 'paddock' && scopeLevel !== 'input';
  // The per-crop spend timeline (inside the Plan vs Actual card) is the
  // inverse — it only makes sense once scoped to (largely) one crop, so it
  // needs Paddock level or lower.
  const showTimeline = scopeLevel === 'paddock' || scopeLevel === 'input';

  const areaRows = useMemo(
    () => (showCropLevelSections ? computeAreaSummary(inputs, gUnits) : []),
    [showCropLevelSections, inputs, gUnits]
  );
  const sections = useMemo(
    () => computeSummaryReportBySide(inputs, gUnits, sides),
    [inputs, gUnits, sides]
  );
  const costByCropRows = useMemo(
    () =>
      showCropLevelSections
        ? computeCostByCropBySide(inputs, gUnits, sides)
        : [],
    [showCropLevelSections, inputs, gUnits, sides]
  );
  const planVsActualRows = useMemo(
    () => computePlanVsActual(inputs, gUnits),
    [inputs, gUnits]
  );

  const navEntries: NavEntry[] = useMemo(() => {
    const entries: NavEntry[] = [];
    if (areaRows.length > 0) entries.push({ id: 'summary2-area', label: 'Crop area' });
    if (costByCropRows.length > 0) {
      entries.push({ id: 'summary2-costByCrop', label: 'Cost by Crop' });
    }
    if (planVsActualRows.length > 0) {
      entries.push({
        id: SUMMARY_PLAN_VS_ACTUAL_SECTION_ID,
        label: 'Plan vs Actual',
      });
    }
    if (sections.length > 0) {
      entries.push({
        id: SUMMARY_INPUT_SUMMARIES_SECTION_ID,
        label: 'Input Summaries',
      });
    }
    return entries;
  }, [areaRows, costByCropRows, planVsActualRows, sections]);

  const handleJump = useCallback((id: string) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Live totals' "All crops / whole farm" link lands here via the URL hash —
  // scroll to the section once it has rendered, then drop the hash so the link
  // works again.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.hash !== `#${SUMMARY_INPUT_SUMMARIES_SECTION_ID}`) return;
    if (!document.getElementById(SUMMARY_INPUT_SUMMARIES_SECTION_ID)) return;
    handleJump(SUMMARY_INPUT_SUMMARIES_SECTION_ID);
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    sections.length,
    handleJump,
    navigate,
  ]);

  const isEmpty =
    areaRows.length === 0 &&
    sections.length === 0 &&
    costByCropRows.length === 0 &&
    planVsActualRows.length === 0;

  return (
    <div className="flex min-w-0 max-w-[100rem] flex-1 flex-col gap-6 pr-4">
      {summaryHintVisible && (
        <Notification
          type="primary"
          className="w-fit"
          closable
          handleClose={handleCloseScopeHint}
          icon={
            <img
              src={OpterraPhoenixLogo}
              alt="Logo"
              className="mt-1 h-4 w-auto brightness-0 invert"
            />
          }
        >
          <span className="text-sm">
            Use <b>Farm / Paddock Tree</b> on the left to set summary scope.
          </span>
        </Notification>
      )}

      {inputs.length > 0 && (
        <div className="sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-1 bg-white px-1 py-2">
          {navEntries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className="cursor-pointer rounded-full border border-transparent bg-gray-smoke px-3 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover"
              onClick={() => handleJump(entry.id)}
            >
              {entry.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-dark">
              Data basis
            </span>
            <Segmented<SummaryDataBasis>
              aria-label="Data basis"
              value={summaryDataBasis}
              onChange={handleDataBasisChange}
              options={DATA_BASIS_OPTIONS}
            />
          </div>
        </div>
      )}

      {isEmpty ? (
        <WorkspaceEmptyState message={SUMMARY_NO_DATA_MESSAGE} />
      ) : (
        <div className="flex flex-col gap-6">
          {showCropLevelSections && (
            <>
              <AreaSummaryTable rows={areaRows} />
              <CostByCropTable rows={costByCropRows} sides={sides} />
            </>
          )}
          <PlanVsActualChart
            inputs={inputs}
            gUnits={gUnits}
            showTimeline={showTimeline}
          />

          {sections.length === 0 && (
            <p className="text-sm text-gray-dark">
              {SUMMARY_DATA_BASIS_NO_DATA_MESSAGE}
            </p>
          )}

          {sections.length > 0 && (
            <div
              id={SUMMARY_INPUT_SUMMARIES_SECTION_ID}
              className="flex scroll-mt-24 flex-wrap items-center gap-4"
            >
              <h2 className="text-lg font-bold text-charcoal">
                Input Summaries
              </h2>
              <div className="flex items-center gap-2">
                <Segmented<SummaryInputView>
                  aria-label="Input summaries view"
                  value={summaryInputView}
                  onChange={handleInputViewChange}
                  options={INPUT_VIEW_OPTIONS}
                />
                {!isChartView && (
                  <Segmented<SummaryTemporalLayout>
                    value={temporalLayout}
                    onChange={(value) => setTemporalLayout(value)}
                    options={[
                      { label: 'Total', value: SUMMARY_TEMPORAL_FLAT },
                      {
                        label: 'Monthly Breakdown',
                        value: SUMMARY_TEMPORAL_MONTHLY,
                      },
                    ]}
                  />
                )}
                {(isChartView || isMonthly) && (
                  <Select<SummaryDataMetric>
                    aria-label="Metric"
                    className="w-36"
                    value={activeMetric}
                    onChange={setMonthlyMetric}
                    options={isChartView ? CHART_METRIC_OPTIONS : METRIC_OPTIONS}
                  />
                )}
              </div>
            </div>
          )}

          {sections.map((section) =>
            section.showMonthlyToggle && isMonthly ? (
              <MonthlySectionTable
                key={section.id}
                category={section.id}
                title={section.title}
                metric={activeMetric}
                metricLabel={METRIC_LABELS[activeMetric]}
                inputs={inputs}
                gUnits={gUnits}
                sides={sides}
              />
            ) : (
              <SummaryReportTable
                key={section.id}
                section={section}
                sides={sides}
                view={summaryInputView}
                chartMetric={activeMetric}
              />
            )
          )}
        </div>
      )}
    </div>
  );
};

export default SummaryReportView;
